/**
 * Build RoomAcoustics / Freq+RT60 payloads for tool auralization.
 * Reuses the same IR synth contract as the product engine.
 */

import type { FreqResponseResponse, RT60Response } from '@/lib/adminTypes';
import type { RoomAcoustics } from '@/lib/audio/types';

const BANDS_ROOM: Record<string, number> = {
  '200': 0.55, '250': 0.55, '315': 0.52, '400': 0.5, '500': 0.48,
  '630': 0.47, '800': 0.46, '1000': 0.45, '1250': 0.44, '1600': 0.43,
  '2000': 0.42, '2500': 0.4, '3150': 0.38, '4000': 0.36, '5000': 0.34,
  '6300': 0.32, '8000': 0.3, '10000': 0.28,
};

const BANDS_DRY: Record<string, number> = {
  '200': 0.22, '250': 0.22, '315': 0.21, '400': 0.2, '500': 0.2,
  '630': 0.19, '800': 0.19, '1000': 0.18, '1250': 0.18, '1600': 0.17,
  '2000': 0.17, '2500': 0.16, '3150': 0.16, '4000': 0.15, '5000': 0.15,
  '6300': 0.14, '8000': 0.14, '10000': 0.13,
};

export interface ToolAcousticsBundle {
  freq: FreqResponseResponse;
  rt60: RT60Response;
  nProducts: number;
}

function fineRt60(freqs: number[], seconds: number): { freqs: number[]; rt60: number[] } {
  return { freqs: [...freqs], rt60: freqs.map(() => seconds) };
}

/**
 * @param magBaseline linear mag for "before" (pure / untreated)
 * @param mag linear mag for "after" (room / treated)
 */
export function buildToolAcoustics(
  freqs: number[],
  magBaseline: number[],
  mag: number[],
  opts?: {
    rt60BaselineSec?: number;
    rt60Sec?: number;
    nProducts?: number;
    treatBroadband?: boolean;
  },
): ToolAcousticsBundle {
  const rt60BaselineSec = opts?.rt60BaselineSec ?? 0.55;
  const rt60Sec = opts?.rt60Sec ?? 0.35;
  const nProducts = opts?.nProducts ?? 6;
  const treatBroadband = opts?.treatBroadband ?? true;

  return {
    freq: {
      freqs: [...freqs],
      mag: [...mag],
      mag_baseline: [...magBaseline],
    },
    rt60: {
      rt60: treatBroadband ? { ...BANDS_DRY } : { ...BANDS_ROOM },
      rt60_fine: fineRt60(freqs, rt60Sec),
      rt60_baseline: { ...BANDS_ROOM },
      rt60_fine_baseline: fineRt60(freqs, rt60BaselineSec),
    },
    nProducts,
  };
}

export interface NamedMagState {
  id: string;
  mag: number[];
  /** Lower = drier / more “pure”. */
  nProducts: number;
  rt60FineSec: number;
  dryBroadband?: boolean;
}

/** Multi-state RoomAcoustics map for useToolAuralization. */
export function buildNamedRoomStates(
  freqs: number[],
  named: NamedMagState[],
): Record<string, RoomAcoustics> {
  const out: Record<string, RoomAcoustics> = {};
  for (const s of named) {
    out[s.id] = {
      freqResponse: { freqs: [...freqs], mag: [...s.mag] },
      rt60Fine: fineRt60(freqs, s.rt60FineSec),
      rt60Bands: s.dryBroadband ? { ...BANDS_DRY } : { ...BANDS_ROOM },
      nProducts: s.nProducts,
    };
  }
  return out;
}

export function flatMagnitude(freqs: number[], level = 1): number[] {
  return freqs.map(() => level);
}
